import { parseLocalDate } from "../../../utils/dateUtils";

const MONTH_ABBR = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DAY_ABBR = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/** Local midnight of a date-only string or Date. Date-only strings are read as local calendar dates, not UTC midnight. */
const startOfDay = (d) => {
  const c = typeof d === "string" ? parseLocalDate(d) || new Date(NaN) : new Date(d);
  c.setHours(0, 0, 0, 0);
  return c;
};

export function parseDateInfo(dateStr) {
  const d = dateStr ? parseLocalDate(dateStr) : null;
  if (!d || Number.isNaN(d.getTime())) return null;
  return { month: MONTH_ABBR[d.getMonth()], day: d.getDate(), weekday: DAY_ABBR[d.getDay()] };
}

export function formatTime(t) {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${h >= 12 ? "PM" : "AM"}`;
}

/** A published session is "live"; an unpublished one is upcoming from today onward and completed before today. */
export function getSessionStatus(session, now = new Date()) {
  if (session.enabled === true) return "live";
  const day = startOfDay(session.sessionDate || session.date || "");
  return day < startOfDay(now) ? "completed" : "upcoming";
}

/**
 * Whether the QR check-in code can be shown for a session right now: `open` on the session's day, `early` before it,
 * `closed` after it, `unavailable` when it is unpublished, locked or undated. Time of day is ignored on purpose (the
 * server clock is UTC and session times are local); the server applies the same day-level rule, with a day of slack.
 */
export function checkinAvailability(session, now = new Date()) {
  if (session.enabled !== true) return { state: "unavailable", reason: "Publish the session to use check-in" };
  if (session.locked) return { state: "unavailable", reason: "This session is locked" };
  const day = startOfDay(session.sessionDate || session.date || "");
  if (Number.isNaN(day.getTime())) return { state: "unavailable", reason: "This session has no date" };
  const today = startOfDay(now);
  if (day > today) return { state: "early", reason: "Opens on the day of the session" };
  if (day < today) return { state: "closed", reason: "This session has passed" };
  return { state: "open", reason: "" };
}

/**
 * Button counts. `live` is published sessions only. `upcoming` matches the Upcoming filter, which also lists those
 * published sessions, so it is unpublished-upcoming plus live.
 */
export function countByStatus(sessions, now = new Date()) {
  const counts = { all: sessions.length, live: 0, upcoming: 0, completed: 0 };
  sessions.forEach((s) => { counts[getSessionStatus(s, now)] += 1; });
  counts.upcoming += counts.live;
  return counts;
}

/**
 * Two groups: published ("live") sessions, then the unpublished ones. The Upcoming filter keeps live sessions and
 * unpublished upcoming ones; Completed shows only unpublished completed ones.
 */
export function groupSessions(sessions, filter, now = new Date()) {
  const items = sessions.map((session) => ({ session, status: getSessionStatus(session, now) }));
  const live = filter === "completed" ? [] : items.filter((i) => i.status === "live");
  const hidden = items.filter((i) => i.status !== "live" && (filter === "all" || i.status === filter));
  return [
    live.length && { key: "live", items: live },
    hidden.length && { key: "hidden", items: hidden },
  ].filter(Boolean);
}

export function sessionTitle(session, allSessions = []) {
  const number = sessionDisplayNumber(session, allSessions);
  return session.name || session.title || (number != null ? `Session ${number}` : "Session");
}

/** Prefer the stored sessionNumber; else the 1-based position in date order — never the raw database id. */
export function sessionDisplayNumber(session, allSessions = []) {
  if (session == null) return null;
  if (session.sessionNumber != null && session.sessionNumber !== "") return session.sessionNumber;
  const time = (x) => new Date(x.sessionDate || x.date || 0).getTime();
  const sorted = [...allSessions].sort((a, b) => time(a) - time(b) || Number(a.id) - Number(b.id));
  const idx = sorted.findIndex((x) => String(x.id) === String(session.id));
  return idx >= 0 ? idx + 1 : null;
}

/** "S01" for 1, "S-003", "S03" or 3; "S—" when there is no number. Stored numbers like "S-001" must not become "SS-001". */
export function sessionSeqLabel(number) {
  if (number == null || number === "") return "S—";
  const digits = /^S?-?0*(\d+)$/i.exec(String(number).trim());
  return digits ? `S${digits[1].padStart(2, "0")}` : String(number);
}

/** Date info for a session, from `sessionDate` or, failing that, the date part of `startDate`. */
export function sessionDateInfo(session) {
  const raw = session?.sessionDate || (session?.startDate ? String(session.startDate).slice(0, 10) : "");
  return parseDateInfo(raw);
}

/** Heading for a session group: name, "Tue, Sep 22", "9:00 AM · Room 100". */
export function sessionHeading(session) {
  const info = sessionDateInfo(session);
  const time = session.startTime ? formatTime(session.startTime) : "";
  return {
    label: session.sessionNumber || `Session ${session.id}`,
    date: info ? `${info.weekday}, ${info.month} ${info.day}` : null,
    detail: [time, session.location].filter(Boolean).join(" · "),
  };
}
